import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../context/SettingsContext';
import { useLanguage } from '../context/LanguageContext';
import { UILanguage } from '../i18n/config';
import { FaGlobe, FaBook, FaCheck } from 'react-icons/fa';

const UserSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { uiLanguage, setUILanguage, availableUILanguages } = useSettings();
  const { selectedLanguage, setSelectedLanguage, availableLanguages } = useLanguage();

  const [selectedUILang, setSelectedUILang] = useState<UILanguage>(uiLanguage);
  const [selectedTargetLang, setSelectedTargetLang] = useState(selectedLanguage);
  const [showToast, setShowToast] = useState(false);

  const hasChanges =
    selectedUILang !== uiLanguage ||
    selectedTargetLang?.iso639_tag !== selectedLanguage?.iso639_tag;

  const handleSave = () => {
    // Save UI language if changed
    if (selectedUILang !== uiLanguage) {
      setUILanguage(selectedUILang);
    }

    // Save target language if changed
    if (selectedTargetLang && selectedTargetLang.iso639_tag !== selectedLanguage?.iso639_tag) {
      setSelectedLanguage(selectedTargetLang);
    }

    // Show success toast
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);

    // Reload to apply all changes (especially target language change)
    if (selectedTargetLang && selectedTargetLang.iso639_tag !== selectedLanguage?.iso639_tag) {
      setTimeout(() => window.location.reload(), 500);
    }
  };

  const handleCancel = () => {
    setSelectedUILang(uiLanguage);
    setSelectedTargetLang(selectedLanguage);
  };

  return (
    <div className="min-h-screen bg-base-100 pt-16 pb-20">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-base-content">{t('settings.title')}</h1>
        </div>

        {/* Settings Sections */}
        <div className="space-y-6">
          {/* UI Language Section */}
          <div className="card bg-base-200 shadow-md">
            <div className="card-body">
              <div className="flex items-center gap-3 mb-4">
                <FaGlobe className="text-2xl text-primary" />
                <div>
                  <h2 className="text-xl font-semibold">{t('settings.uiLanguage')}</h2>
                  <p className="text-sm text-base-content/70">
                    {t('settings.uiLanguageDescription')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {Object.entries(availableUILanguages).map(([code, name]) => (
                  <button
                    key={code}
                    onClick={() => setSelectedUILang(code as UILanguage)}
                    className={`btn btn-outline justify-start ${
                      selectedUILang === code ? 'btn-primary' : ''
                    }`}
                  >
                    {selectedUILang === code && <FaCheck className="mr-2" />}
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Target Language Section */}
          <div className="card bg-base-200 shadow-md">
            <div className="card-body">
              <div className="flex items-center gap-3 mb-4">
                <FaBook className="text-2xl text-primary" />
                <div>
                  <h2 className="text-xl font-semibold">{t('settings.targetLanguage')}</h2>
                  <p className="text-sm text-base-content/70">
                    {t('settings.targetLanguageDescription')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {availableLanguages.map((language) => (
                  <button
                    key={language.iso639_tag}
                    onClick={() => setSelectedTargetLang(language)}
                    className={`btn btn-outline justify-start ${
                      selectedTargetLang?.iso639_tag === language.iso639_tag ? 'btn-primary' : ''
                    }`}
                  >
                    <span className="text-xl mr-2">{language.flag}</span>
                    {selectedTargetLang?.iso639_tag === language.iso639_tag && (
                      <FaCheck className="mr-2" />
                    )}
                    {language.language_name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        {hasChanges && (
          <div className="fixed bottom-20 left-0 right-0 bg-base-200 border-t border-base-300 p-4 z-40">
            <div className="max-w-2xl mx-auto flex gap-3 justify-end">
              <button onClick={handleCancel} className="btn btn-ghost">
                {t('settings.cancel')}
              </button>
              <button onClick={handleSave} className="btn btn-primary">
                {t('settings.save')}
              </button>
            </div>
          </div>
        )}

        {/* Success Toast */}
        {showToast && (
          <div className="toast toast-top toast-center z-50">
            <div className="alert alert-success">
              <FaCheck />
              <span>{t('settings.saved')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSettingsPage;
